<script setup lang="ts">
import type { Ref } from 'vue'
import { compile, computed, getCurrentInstance, onMounted, ref } from 'vue'

const props = defineProps({
  text: {
    type: String,
    required: true,
  }
})

const componentRoot: Ref<HTMLElement | null> = ref(null)
const richtext = computed(() => compile(props.text))
const hasGlossary = computed(() => {
  const glossaryRegex = /<glossary[\s\S]*?<\/glossary>/
  return glossaryRegex.test(props.text)
})

/**
 * This function adds a span with the whitespace-nowrap class to the glossary and its next sibling
 * if the next sibling is a text node starting with a punctuation character. This should avoid line
 * breaks between the glossary and the punctuation character that is caused by the normal flow of
 * inline elements.
 */
const processGlossaries = async () => {
  if (!hasGlossary.value) {
    return
  }

  const instance = getCurrentInstance()
  const domElement = instance?.refs.componentRoot ?? null

  if (!componentRoot.value || !(domElement instanceof HTMLElement)) {
    const parentNode = componentRoot.value?.parentNode
    if (!(parentNode instanceof HTMLElement))
      return

    componentRoot.value = parentNode as HTMLElement
  }

  const glossaries = componentRoot.value.querySelectorAll('.glossary')

  glossaries.forEach((glossary) => {
    const nextSibling = glossary.nextSibling
    if (
      nextSibling
      && nextSibling.nodeType === Node.TEXT_NODE
      && /^[.,!?;:]/.test(nextSibling.nodeValue?.charAt(0) || '')
    ) {
      const wrapper = document.createElement('span')
      wrapper.classList.add('whitespace-nowrap')

      const firstChar = nextSibling.nodeValue?.charAt(0) || ''
      nextSibling.nodeValue = nextSibling.nodeValue?.substring(1) || ''

      glossary.parentNode?.insertBefore(wrapper, glossary)
      wrapper.appendChild(glossary)
      wrapper.appendChild(document.createTextNode(firstChar))
    }
  })
}

onMounted(processGlossaries)
</script>

<template>
  <richtext ref="componentRoot" />
</template>
